// src/components/ClientsInstructions.js
// Clients B2B — /clients-instructions. The merchants the logistician ships
// for (clients_edn): how each one is recognised on incoming tickets, what the
// agent applies to it, whether it is automated. « À vérifier » lists the
// configuration gaps and the aliases the AI added; the tiles filter the list.
// Views (?view=): « Clients » (the list), « Activité » (ticket volume per
// client over 7 / 30 / 90 days, GET /api/statistics/overview → top_clients,
// fetched only on that view) and « Avis de souffrance » (the general settings,
// GET/PUT /api/logistics/souffrance-config). List state lives in the URL
// (?q, ?page, ?size, ?sort=-name, ?filter); ?new=1 opens the creation.
// API: GET/POST /api/logistics/clients-edn, GET /api/logistics/carriers.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, BarChart3, BellRing, Bot, Building2, ChevronRight, Plus, RefreshCw, ScrollText, Search,
  Sparkles, TriangleAlert, X,
} from 'lucide-react';
import {
  Badge, Banner, Card, EmptyState, Field, Kpi, Meter, Modal, PageHeader, Pagination, Segmented, Tabs, Toast,
} from '../design/ui';
import { ClientB2BForm, languageLabel } from './ClientB2BFields';
import {
  LIST_FILTERS, SOUFFRANCE_LANGUAGES, b2bAttention, b2bListSummary, buildCreatePayload,
  emptyB2BForm, matchTopClients, matchesFilter, matchesSearch, souffranceConfigured,
} from './clientsB2B';
import { formatCount, formatPercent, formatShortDay, formatStamp, nextParams } from './clientsShared';
import './ClientsInstructions.css';

const PAGE_SIZES = [25, 50, 100];
const VIEWS = ['clients', 'activity', 'souffrance'];
const ACTIVITY_DAYS = [7, 30, 90];
const SEGMENT_FILTERS = ['all', 'auto', 'manual'];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const ClientsInstructions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL state — back from a client restores the list as it was.
  const searchQuery = searchParams.get('q') || '';
  const sortDesc = searchParams.get('sort') === '-name';
  const sizeParam = Number(searchParams.get('size'));
  const pageSize = PAGE_SIZES.includes(sizeParam) ? sizeParam : 25;
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const requestedPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const filter = LIST_FILTERS.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all';
  const view = VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'clients';
  const daysParam = Number(searchParams.get('days'));
  const days = ACTIVITY_DAYS.includes(daysParam) ? daysParam : 30;

  const updateParams = useCallback((updates, { replace = true } = {}) => {
    setSearchParams((previous) => nextParams(previous, updates), { replace });
  }, [setSearchParams]);

  const [clients, setClients] = useState(null); // null = loading
  const [listError, setListError] = useState(null);
  const [carriers, setCarriers] = useState(null);
  const [toast, setToast] = useState(null);

  // Creation (?new=1 opens it: a link can start a creation).
  const [showCreateModal, setShowCreateModal] = useState(() => searchParams.get('new') === '1');
  const [newClient, setNewClient] = useState(emptyB2BForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // General souffrance settings.
  const [souffrance, setSouffrance] = useState(undefined); // undefined = loading, null = unavailable
  const [souffranceForm, setSouffranceForm] = useState({ language: '', instructions: '', signature: '' });
  const [souffranceSaving, setSouffranceSaving] = useState(false);
  const [souffranceError, setSouffranceError] = useState(null);

  // Activity (only fetched on its view).
  const [overview, setOverview] = useState(undefined);

  const showToast = (tone, text) => setToast({ tone, text });
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchClients = useCallback(async () => {
    setListError(null);
    try {
      const response = await axios.get('/api/logistics/clients-edn', { headers: authHeaders() });
      setClients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      const detail = error.response?.data?.detail;
      setListError(typeof detail === 'string' ? detail : t('clientsUi.list.loadError', 'Impossible de charger les clients B2B.'));
      setClients((previous) => previous || []);
    }
  }, [t]);

  const fetchCarriers = useCallback(async () => {
    try {
      const response = await axios.get('/api/logistics/carriers', { headers: authHeaders() });
      setCarriers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch carriers:', error);
      setCarriers([]);
    }
  }, []);

  const fetchSouffranceConfig = useCallback(async () => {
    try {
      const response = await axios.get('/api/logistics/souffrance-config', { headers: authHeaders() });
      const data = response.data || {};
      setSouffrance(data);
      setSouffranceForm({ language: data.language || '', instructions: data.instructions || '', signature: data.signature || '' });
    } catch (error) {
      console.error('Failed to fetch souffrance settings:', error);
      setSouffrance(null);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchCarriers();
    fetchSouffranceConfig();
  }, [fetchClients, fetchCarriers, fetchSouffranceConfig]);

  useEffect(() => {
    if (view !== 'activity') return undefined;
    let cancelled = false;
    setOverview(undefined);
    axios.get(`/api/statistics/overview?days=${days}`, { headers: authHeaders() })
      .then((response) => { if (!cancelled) setOverview(response.data || {}); })
      .catch((error) => {
        console.error('Failed to fetch client activity:', error);
        if (!cancelled) setOverview(null);
      });
    return () => { cancelled = true; };
  }, [view, days]);

  const refresh = () => {
    fetchClients();
    fetchCarriers();
    fetchSouffranceConfig();
  };

  // ---- creation -------------------------------------------------------------

  const openCreate = (prefill = {}) => {
    setNewClient({ ...emptyB2BForm(), ...prefill });
    setCreateError(null);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    setCreateError(null);
    if (searchParams.get('new')) updateParams({ new: null });
  };

  const handleCreateClient = async (event) => {
    if (event) event.preventDefault();
    if (!newClient.name.trim()) {
      setCreateError(t('clientsInstructions.nameRequired', 'Le nom du client est obligatoire.'));
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await axios.post('/api/logistics/clients-edn', buildCreatePayload(newClient), { headers: authHeaders() });
      const name = newClient.name.trim();
      setShowCreateModal(false);
      setNewClient(emptyB2BForm());
      if (searchParams.get('new')) updateParams({ new: null });
      showToast('success', t('clientsUi.create.done', 'Client « {{name}} » ajouté.', { name }));
      fetchClients();
    } catch (error) {
      console.error('Failed to create client:', error);
      const detail = error.response?.data?.detail;
      setCreateError(typeof detail === 'string'
        ? detail
        : t('clientsInstructions.createError', 'Échec de la création du client.'));
    } finally {
      setCreating(false);
    }
  };

  // ---- general souffrance settings -----------------------------------------

  const souffranceDirty = Boolean(souffrance) && (
    souffranceForm.language !== (souffrance.language || '')
    || souffranceForm.instructions !== (souffrance.instructions || '')
    || souffranceForm.signature !== (souffrance.signature || '')
  );

  const handleSaveSouffranceConfig = async (event) => {
    if (event) event.preventDefault();
    setSouffranceSaving(true);
    setSouffranceError(null);
    try {
      const response = await axios.put(
        '/api/logistics/souffrance-config',
        {
          language: souffranceForm.language || null,
          instructions: souffranceForm.instructions || null,
          // The backend keeps the stored signature on null: '' clears it.
          signature: souffranceForm.signature.trim(),
        },
        { headers: authHeaders() },
      );
      const data = response.data || {};
      setSouffrance(data);
      setSouffranceForm({ language: data.language || '', instructions: data.instructions || '', signature: data.signature || '' });
      showToast('success', t('souffranceSettings.saved', 'Réglages de souffrance enregistrés.'));
    } catch (error) {
      console.error('Failed to save souffrance settings:', error);
      const detail = error.response?.data?.detail;
      setSouffranceError(typeof detail === 'string'
        ? detail
        : t('souffranceSettings.saveError', 'Échec de l\'enregistrement des réglages de souffrance.'));
    } finally {
      setSouffranceSaving(false);
    }
  };

  // ---- derived --------------------------------------------------------------

  const loading = clients === null;
  const summary = useMemo(() => b2bListSummary(clients || []), [clients]);
  const attention = useMemo(() => (clients ? b2bAttention(clients, carriers) : []), [clients, carriers]);

  const visible = useMemo(() => (clients || [])
    .filter((client) => matchesFilter(client, filter) && matchesSearch(client, searchQuery))
    .sort((a, b) => {
      const cmp = (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    }), [clients, filter, searchQuery, sortDesc]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(requestedPage, totalPages);
  const pageRows = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const setFilter = (next) => updateParams({ filter: next === 'all' ? null : next, page: null, view: null });
  const setView = (next) => updateParams({ view: next === 'clients' ? null : next });
  const openClient = (client) => navigate(`/clients-instructions/${client.id}`);

  const filterLabels = {
    all: t('clientsUi.filters.all', 'Tous'),
    auto: t('clientsUi.filters.auto', 'Automatisés'),
    manual: t('clientsUi.filters.manual', 'Manuels'),
    noemail: t('clientsUi.filters.noemail', 'Sans adresse ni domaine'),
    noinstr: t('clientsUi.filters.noinstr', 'Sans consigne'),
    ai_alias: t('clientsUi.filters.ai_alias', 'Alias ajoutés par l’IA'),
    excluded: t('clientsUi.filters.excluded', 'Avec transporteurs exclus'),
  };

  // ---- zones ----------------------------------------------------------------

  const subtitle = loading
    ? t('clientsUi.list.subtitleLoading', 'Les entreprises pour lesquelles vous expédiez.')
    : summary.total === 0
      ? t('clientsUi.list.subtitleEmpty', 'Les entreprises pour lesquelles vous expédiez : leurs tickets leur sont rattachés à l’arrivée.')
      : t('clientsUi.list.subtitle', '{{total}} clients · {{automated}} automatisés · {{withEmails}} reconnus par e-mail', {
        total: formatCount(summary.total), automated: formatCount(summary.automated), withEmails: formatCount(summary.withEmails),
      });

  const attentionText = (item) => {
    switch (item.kind) {
      case 'noemail':
        return {
          title: t('clientsUi.attention.noemail', '{{count}} clients sans adresse ni domaine e-mail', { count: item.count }),
          detail: t('clientsUi.attention.noemailDetail', 'Leurs e-mails entrants ne leur sont pas rattachés automatiquement : {{names}}', { names: item.names.join(', ') }),
        };
      case 'noinstr':
        return {
          title: t('clientsUi.attention.noinstr', '{{count}} clients automatisés sans consigne', { count: item.count }),
          detail: t('clientsUi.attention.noinstrDetail', 'L’agent agit seul avec vos seules consignes générales : {{names}}', { names: item.names.join(', ') }),
        };
      case 'ai_alias':
        return {
          title: t('clientsUi.attention.aiAlias', 'L’IA a ajouté {{count}} alias ces 14 derniers jours', { count: item.count }),
          detail: t('clientsUi.attention.aiAliasDetail', 'Enseignes vérifiées sur un colis — relisez-les : {{names}}', { names: item.names.join(' · ') }),
        };
      case 'stale':
        return {
          title: t('clientsUi.attention.stale', '{{name}} exclut un transporteur que vous n’avez plus', { name: item.clientName }),
          detail: t('clientsUi.attention.staleDetail', 'Exclusion devenue sans effet : {{carriers}}. Retirez-la de sa fiche.', { carriers: item.carriers.join(', ') }),
        };
      default:
        return { title: item.kind, detail: '' };
    }
  };

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('clientsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="clients-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => {
            const text = attentionText(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>
                  {item.tone === 'info' ? t('clientsUi.attention.review', 'À relire') : t('clientsUi.attention.watch', 'À surveiller')}
                </Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{text.title}</div>
                  <div className="u-row__meta" title={text.detail}>{text.detail}</div>
                </div>
                <div className="u-row__side">
                  <button
                    type="button"
                    className="u-btn u-btn--secondary u-btn--sm"
                    onClick={() => (item.clientId ? navigate(`/clients-instructions/${item.clientId}?tab=configuration`) : setFilter(item.filter))}
                  >
                    {t('clientsUi.see', 'Voir')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (loading) {
      return (
        <div className="u-kpi-row clients-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => <span key={i} className="u-skeleton clients-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (summary.total === 0) return null;
    const souffranceOn = souffranceConfigured(souffrance);
    return (
      <div className={`u-kpi-row${souffrance ? ' clients-v2__kpis' : ''}`}>
        <Kpi
          icon={Building2}
          label={t('clientsUi.kpi.clients', 'Clients B2B')}
          value={formatCount(summary.total)}
          context={t('clientsUi.kpi.clientsContext', '{{count}} reconnus par e-mail', { count: summary.withEmails })}
          meter={{ value: summary.withEmails, max: summary.total, label: t('clientsUi.kpi.clientsContext', '{{count}} reconnus par e-mail', { count: summary.withEmails }) }}
          active={view === 'clients' && filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <Kpi
          icon={Bot}
          tone={summary.automated ? 'success' : 'neutral'}
          label={t('clientsUi.kpi.automation', 'Automatisés')}
          value={formatCount(summary.automated)}
          unit={`/ ${formatCount(summary.total)}`}
          context={t('clientsUi.kpi.automationContext', '{{count}} traités par votre équipe', { count: summary.manual })}
          meter={{ value: summary.automated, max: summary.total, tone: 'success', label: t('clientsUi.kpi.automation', 'Automatisés') }}
          active={view === 'clients' && filter === 'auto'}
          onClick={() => setFilter('auto')}
        />
        <Kpi
          icon={ScrollText}
          tone={summary.automatedNoInstructions ? 'warning' : 'success'}
          label={t('clientsUi.kpi.instructions', 'Avec consignes')}
          value={formatCount(summary.withInstructions)}
          unit={`/ ${formatCount(summary.total)}`}
          context={summary.automatedNoInstructions
            ? t('clientsUi.kpi.instructionsWarn', '{{count}} automatisés sans consigne', { count: summary.automatedNoInstructions })
            : t('clientsUi.kpi.instructionsContext', '{{count}} sans consigne propre', { count: summary.noInstructions })}
          contextTone={summary.automatedNoInstructions ? 'warning' : undefined}
          active={view === 'clients' && filter === 'noinstr'}
          onClick={() => setFilter('noinstr')}
        />
        <Kpi
          icon={Sparkles}
          label={t('clientsUi.kpi.aiAliases', 'Alias de l’IA')}
          value={formatCount(summary.aiAliases)}
          context={summary.recentAiAliases.length
            ? t('clientsUi.kpi.aiAliasesRecent', '{{count}} récents à relire (14 jours)', { count: summary.recentAiAliases.length })
            : t('clientsUi.kpi.aiAliasesContext', 'enseignes vérifiées sur un colis, chez {{count}} clients', { count: summary.aiAliasClients })}
          active={view === 'clients' && filter === 'ai_alias'}
          onClick={() => setFilter('ai_alias')}
        />
        {souffrance !== undefined && souffrance !== null && (
          <Kpi
            icon={BellRing}
            tone={souffranceOn ? undefined : 'neutral'}
            label={t('clientsUi.kpi.souffrance', 'Souffrance')}
            value={souffrance.language ? languageLabel(t, souffrance.language) : t('clientEDNDetail.languages.French', 'Français')}
            context={souffranceOn
              ? t('clientsUi.kpi.souffranceOn', 'avis personnalisés')
              : t('clientsUi.kpi.souffranceOff', 'avis par défaut')}
            active={view === 'souffrance'}
            onClick={() => setView('souffrance')}
          />
        )}
      </div>
    );
  };

  // The first address or domain, then « +N »: what the row is recognised by.
  const recognitionCell = (client) => {
    const entries = (client.emails || []).filter((entry) => String(entry || '').trim());
    if (!entries.length) return <Badge tone="warning" dot>{t('clientsUi.list.noDetection', 'Aucune')}</Badge>;
    return (
      <span className="clients-v2__recognition" title={entries.join(', ')}>
        <span className="clients-v2__recognition-first">{entries[0]}</span>
        {entries.length > 1 && <span className="clients-v2__more">+{entries.length - 1}</span>}
      </span>
    );
  };

  const clientSub = (client) => {
    const aliases = client.aliases || [];
    const aiCount = (client.alias_provenance || []).length;
    const parts = [];
    if (aliases.length) parts.push(t('clientsUi.list.aliases', '{{count}} alias', { count: aliases.length }));
    if (client.reference) parts.push(t('clientsUi.list.reference', 'Réf. {{reference}}', { reference: client.reference }));
    if (!parts.length && !aiCount) return null;
    return (
      <span className="u-cell-sub clients-v2__sub">
        {aiCount > 0 && (
          <span className="clients-v2__ai" title={t('clientsUi.list.aiAliasesTitle', '{{count}} alias ajoutés par l’IA', { count: aiCount })}>
            <Sparkles strokeWidth={1.8} aria-label={t('clientsUi.list.aiAliasesTitle', '{{count}} alias ajoutés par l’IA', { count: aiCount })} />
          </span>
        )}
        {parts.join(' · ')}
      </span>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="clients-v2__skeleton" aria-busy="true" aria-label={t('clientsUi.list.loading', 'Chargement des clients')}>
          {Array.from({ length: 8 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!clients.length) {
      if (listError) return null;
      return (
        <EmptyState
          icon={Building2}
          title={t('clientsUi.list.emptyTitle', 'Aucun client B2B pour l’instant')}
          action={(
            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => openCreate()}>
              <Plus aria-hidden="true" />
              {t('clientsUi.list.add', 'Ajouter un client')}
            </button>
          )}
        >
          {t('clientsUi.list.emptyHint', 'Ajoutez les entreprises pour lesquelles vous expédiez : leurs tickets leur seront rattachés à l’arrivée, avec leurs consignes et leurs transporteurs.')}
        </EmptyState>
      );
    }
    if (!visible.length) {
      return (
        <EmptyState
          icon={Search}
          title={t('clientsUi.list.noMatch', 'Aucun client ne correspond')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateParams({ q: null, filter: null, page: null })}>
              {t('clientsUi.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        >
          {t('clientsUi.list.noMatchHint', 'Changez la recherche ou le filtre.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table clients-v2__table">
          <thead>
            <tr>
              <th style={{ width: '29%' }} aria-sort={sortDesc ? 'descending' : 'ascending'}>
                <button type="button" className="clients-v2__sort" onClick={() => updateParams({ sort: sortDesc ? null : '-name' })}>
                  {t('clientsUi.list.colClient', 'Client')}
                  {sortDesc ? <ArrowDown aria-hidden="true" /> : <ArrowUp aria-hidden="true" />}
                </button>
              </th>
              <th style={{ width: '17%' }}>{t('clientsUi.list.colRecognition', 'Reconnaissance')}</th>
              <th style={{ width: '15%' }}>{t('clientsUi.list.colInstructions', 'Consignes')}</th>
              <th style={{ width: '16%' }}>{t('clientsUi.list.colAutomation', 'Automatisation')}</th>
              <th style={{ width: '12%' }}>{t('clientsUi.list.colExcluded', 'Transp. exclus')}</th>
              <th style={{ width: '11%' }}>{t('clientsUi.list.colUpdated', 'Modifié')}</th>
              <th style={{ width: '62px' }} aria-label={t('clientsUi.open', 'Ouvrir')} />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((client) => (
              <tr
                key={client.id}
                className="clients-v2__row"
                tabIndex={0}
                onClick={() => openClient(client)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openClient(client);
                  }
                }}
              >
                <td title={client.name}>
                  <span className="clients-v2__name">
                    <span className="clients-v2__name-text">{client.name}</span>
                    {client.client_id && <span className="u-code">{client.client_id}</span>}
                  </span>
                  {clientSub(client)}
                </td>
                <td>{recognitionCell(client)}</td>
                <td>
                  {client.instructions && client.instructions.trim()
                    ? <Badge tone="success">{t('clientsUi.list.instructionsSet', 'Renseignées')}</Badge>
                    : client.automation
                      ? <Badge tone="warning" dot>{t('clientsUi.list.instructionsMissing', 'Manquantes')}</Badge>
                      : <span className="u-muted">{t('clientsUi.list.instructionsNone', 'Aucune')}</span>}
                </td>
                <td>
                  {client.automation
                    ? <Badge tone="success" dot>{t('clientsUi.automationOn', 'Active')}</Badge>
                    : <Badge>{t('clientsUi.automationManual', 'Manuelle')}</Badge>}
                  {client.automation && client.automation_updated_at && (
                    <span className="u-cell-sub">{t('clientsUi.list.since', 'depuis le {{date}}', { date: formatShortDay(client.automation_updated_at) })}</span>
                  )}
                </td>
                <td title={(client.disabled_carriers || []).join(', ')}>
                  {(client.disabled_carriers || []).length
                    ? t('clientsUi.list.excludedCount', '{{count}} exclus', { count: client.disabled_carriers.length })
                    : <span className="u-muted">—</span>}
                </td>
                <td className="u-num" title={formatStamp(client.updated_at)}>{formatShortDay(client.updated_at) || <span className="u-muted">—</span>}</td>
                <td className="clients-v2__chevron"><ChevronRight aria-hidden="true" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderClientsView = () => (
    <section className="u-card">
      <div className="u-toolbar">
        <div className="u-input-group u-input-group--lead">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            type="search"
            className="u-input"
            value={searchQuery}
            onChange={(e) => updateParams({ q: e.target.value, page: null })}
            placeholder={t('clientsUi.list.search', 'Nom, identifiant, alias ou domaine')}
            aria-label={t('common.search', 'Rechercher')}
          />
        </div>
        <Segmented
          label={t('clientsUi.list.filterLabel', 'Filtrer les clients')}
          value={SEGMENT_FILTERS.includes(filter) ? filter : null}
          onChange={setFilter}
          options={SEGMENT_FILTERS.map((key) => ({
            value: key,
            label: filterLabels[key],
            count: loading ? undefined : formatCount(key === 'all' ? summary.total : key === 'auto' ? summary.automated : summary.manual),
          }))}
        />
        {!SEGMENT_FILTERS.includes(filter) && (
          <span className="clients-v2__active-filter">
            <Badge tone="accent">{filterLabels[filter]}</Badge>
            <button
              type="button"
              className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
              onClick={() => setFilter('all')}
              aria-label={t('clientsUi.clearFilter', 'Retirer le filtre')}
              title={t('clientsUi.clearFilter', 'Retirer le filtre')}
            >
              <X aria-hidden="true" />
            </button>
          </span>
        )}
        <span className="u-toolbar__spacer" />
        {!loading && (
          <span className="clients-v2__count">
            {t('clientsUi.list.shown', '{{count}} affichés', { count: formatCount(visible.length) })}
          </span>
        )}
      </div>
      {renderTable()}
      {!loading && visible.length > 0 && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          total={visible.length}
          onChange={(next) => updateParams({ page: next <= 1 ? null : next }, { replace: false })}
          summary={({ first, last, total }) => (
            <span className="clients-v2__pager-summary">
              {t('clientsUi.pageSummary', '{{first}}–{{last}} sur {{total}}', { first, last, total: formatCount(total) })}
              <select
                className="u-select clients-v2__size"
                value={pageSize}
                onChange={(e) => updateParams({ size: Number(e.target.value) === 25 ? null : e.target.value, page: null })}
                aria-label={t('clientsUi.perPage', 'Lignes par page')}
              >
                {PAGE_SIZES.map((size) => <option key={size} value={size}>{t('clientsUi.perPageOption', '{{count}} par page', { count: size })}</option>)}
              </select>
            </span>
          )}
          previousLabel={t('common.previousPage', 'Page précédente')}
          nextLabel={t('common.nextPage', 'Page suivante')}
        />
      )}
    </section>
  );

  const renderActivityView = () => {
    const rows = overview ? matchTopClients(overview.top_clients, clients || []) : [];
    const withClient = overview?.top_clients?.total_with_client;
    return (
      <Card
        icon={BarChart3}
        title={t('clientsUi.activity.title', 'Tickets par client')}
        description={overview && withClient !== undefined
          ? t('clientsUi.activity.desc', 'Les clients qui ont ouvert le plus de tickets sur {{days}} jours · {{total}} tickets rattachés à un client sur la période.', { days, total: formatCount(withClient) })
          : t('clientsUi.activity.descLoading', 'Les clients qui ont ouvert le plus de tickets sur {{days}} jours.', { days })}
        actions={(
          <>
            <Segmented
              label={t('clientsUi.activity.period', 'Période')}
              value={days}
              onChange={(next) => updateParams({ days: next === 30 ? null : next })}
              options={ACTIVITY_DAYS.map((value) => ({ value, label: t('clientsUi.activity.days', '{{count}} jours', { count: value }) }))}
            />
            <Link className="u-btn u-btn--ghost u-btn--sm" to={`/statistics?days=${days}`}>
              {t('clientsUi.activity.allStats', 'Statistiques')}
              <ChevronRight aria-hidden="true" />
            </Link>
          </>
        )}
        flush
      >
        {overview === undefined ? (
          <div className="clients-v2__skeleton" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
          </div>
        ) : overview === null ? (
          <div className="clients-v2__pad">
            <Banner tone="danger">{t('clientsUi.activity.error', 'Impossible de charger l’activité des clients.')}</Banner>
          </div>
        ) : !rows.length ? (
          <EmptyState icon={BarChart3} title={t('clientsUi.activity.empty', 'Aucun ticket rattaché à un client sur la période')}>
            {t('clientsUi.activity.emptyHint', 'Le volume par client apparaît dès qu’un ticket porte le nom d’un client.')}
          </EmptyState>
        ) : (
          <div className="u-table-scroll">
            <table className="u-table clients-v2__activity">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>{t('clientsUi.list.colClient', 'Client')}</th>
                  <th style={{ width: '30%' }}>{t('clientsUi.activity.colTickets', 'Tickets')}</th>
                  <th style={{ width: '13%' }} className="clients-v2__num">{t('clientsUi.activity.colResolved', 'Résolus')}</th>
                  <th style={{ width: '13%' }} className="clients-v2__num">{t('clientsUi.activity.colAi', 'Par l’IA')}</th>
                  <th style={{ width: '16%' }} aria-label={t('clientsUi.open', 'Ouvrir')} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.name}>
                    <td title={row.name}>
                      <span className="clients-v2__name-text">{row.name}</span>
                      {row.client && row.client.name !== row.name && (
                        <span className="u-cell-sub">{t('clientsUi.activity.aliasOf', 'alias de {{name}}', { name: row.client.name })}</span>
                      )}
                      {!row.client && <span className="u-cell-sub">{t('clientsUi.activity.noPage', 'Pas de fiche client')}</span>}
                    </td>
                    <td>
                      <span className="clients-v2__bar-cell">
                        <span className="clients-v2__bar-value u-num">{formatCount(row.total)}</span>
                        <Meter value={row.total} max={row.max} label={t('clientsUi.activity.colTickets', 'Tickets')} />
                        <span className="clients-v2__bar-share u-num">{formatPercent(row.share / 100)}</span>
                      </span>
                    </td>
                    <td className="clients-v2__num" title={t('clientsUi.activity.resolvedTitle', '{{count}} résolus', { count: row.resolved })}>{formatPercent(row.resolutionRate)}</td>
                    <td className="clients-v2__num" title={t('clientsUi.activity.aiTitle', '{{count}} résolus par l’IA', { count: row.aiResolved })}>{formatPercent(row.aiShare)}</td>
                    <td className="clients-v2__actions-cell">
                      {row.client ? (
                        <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => openClient(row.client)}>
                          {t('clientsUi.open', 'Ouvrir')}
                          <ChevronRight aria-hidden="true" />
                        </button>
                      ) : (
                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openCreate({ name: row.name })}>
                          <Plus aria-hidden="true" />
                          {t('clientsUi.activity.createPage', 'Créer la fiche')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    );
  };

  const renderSouffranceView = () => {
    const own = (clients || []).filter((client) => client.souffrance_language || client.souffrance_instructions);
    return (
      <div className="clients-v2__souffrance">
        <Card
          icon={BellRing}
          title={t('souffranceSettings.title', 'Instructions générales de souffrance')}
          badge={souffrance ? (
            souffranceConfigured(souffrance)
              ? <Badge tone="accent">{t('souffranceSettings.configured', 'Configuré')}</Badge>
              : <Badge>{t('souffranceSettings.notConfigured', 'Par défaut')}</Badge>
          ) : null}
          description={t('souffranceSettings.subtitle', 'Appliquées à chaque notification de souffrance, pour tous les clients. Ouvrez la page d\'un client pour remplacer la langue ou ajouter des instructions spécifiques.')}
          footerNote={t('clientsUi.souffrance.footer', 'S’applique aux prochains avis envoyés.')}
          footer={(
            <button
              type="submit"
              form="clients-souffrance-form"
              className="u-btn u-btn--primary"
              disabled={!souffrance || souffranceSaving || !souffranceDirty}
            >
              {souffranceSaving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
            </button>
          )}
        >
          {souffrance === undefined ? (
            <div className="clients-v2__skeleton" aria-busy="true">
              {Array.from({ length: 3 }, (_, i) => <span key={i} className="u-skeleton" />)}
            </div>
          ) : souffrance === null ? (
            <Banner tone="danger">{t('clientsUi.souffrance.loadError', 'Impossible de charger vos réglages de souffrance.')}</Banner>
          ) : (
            <form id="clients-souffrance-form" className="u-form" onSubmit={handleSaveSouffranceConfig}>
              {souffranceError && <Banner tone="danger">{souffranceError}</Banner>}
              <Field label={t('clientsUi.souffrance.language', 'Langue des avis')} htmlFor="souffrance-language">
                <select
                  id="souffrance-language"
                  className="u-select"
                  value={souffranceForm.language}
                  onChange={(e) => { setSouffranceForm((f) => ({ ...f, language: e.target.value })); setSouffranceError(null); }}
                >
                  <option value="">{t('clientsUi.form.languageDefault', 'Par défaut (français)')}</option>
                  {SOUFFRANCE_LANGUAGES.map((language) => (
                    <option key={language} value={language}>{languageLabel(t, language)}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('souffranceSettings.generalInstructions', 'Instructions générales')} htmlFor="souffrance-instructions">
                <textarea
                  id="souffrance-instructions"
                  className="u-textarea"
                  rows={5}
                  value={souffranceForm.instructions}
                  onChange={(e) => { setSouffranceForm((f) => ({ ...f, instructions: e.target.value })); setSouffranceError(null); }}
                  placeholder={t('clientEDNDetail.souffranceInstructionsPlaceholder', 'Ex. toujours mentionner notre numéro de support ; demander un choix de point relais en cas d\'échec de livraison...')}
                />
              </Field>
              <Field
                label={t('souffranceSettings.signature', 'Signature')}
                htmlFor="souffrance-signature"
                help={t('souffranceSettings.signatureHelp', 'Termine chaque avis transporteur que la plateforme envoie à vos clients. Vide, les avis partent sans signature.')}
              >
                <input
                  id="souffrance-signature"
                  type="text"
                  className="u-input"
                  maxLength={300}
                  value={souffranceForm.signature}
                  onChange={(e) => { setSouffranceForm((f) => ({ ...f, signature: e.target.value })); setSouffranceError(null); }}
                  placeholder={t('souffranceSettings.signaturePlaceholder', 'Ex. : Service client, Votre société')}
                />
              </Field>
            </form>
          )}
        </Card>
        <Card
          icon={Building2}
          title={t('clientsUi.souffrance.ownTitle', 'Clients avec leurs propres réglages')}
          badge={clients ? <Badge>{formatCount(own.length)}</Badge> : null}
          description={t('clientsUi.souffrance.ownDesc', 'Leur langue remplace la vôtre ; leurs consignes s’ajoutent aux vôtres.')}
          flush
        >
          {!clients ? null : own.length === 0 ? (
            <EmptyState title={t('clientsUi.souffrance.ownEmpty', 'Aucun client n’a de réglage propre')}>
              {t('clientsUi.souffrance.ownEmptyHint', 'Tous vos clients reçoivent les avis avec vos réglages généraux.')}
            </EmptyState>
          ) : (
            <ul className="u-list">
              {own.map((client) => (
                <li className="u-row" key={client.id}>
                  <div className="u-row__main">
                    <div className="u-row__title">
                      {client.name}
                      {client.souffrance_language && <Badge tone="info">{languageLabel(t, client.souffrance_language)}</Badge>}
                    </div>
                    <div className="u-row__meta" title={client.souffrance_instructions || ''}>
                      {client.souffrance_instructions || t('clientsUi.souffrance.languageOnly', 'Langue seulement')}
                    </div>
                  </div>
                  <div className="u-row__side">
                    <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => navigate(`/clients-instructions/${client.id}?tab=configuration`)}>
                      {t('clientsUi.open', 'Ouvrir')}
                      <ChevronRight aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  };

  return (
    <div className="u-page clients-v2">
      <PageHeader
        title={t('clientsInstructions.pageTitle', 'Clients B2B')}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={refresh}
              title={t('clientsUi.refresh', 'Actualiser')}
              aria-label={t('clientsUi.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button type="button" className="u-btn u-btn--primary" data-tour="clients-create" onClick={() => openCreate()}>
              <Plus aria-hidden="true" />
              {t('clientsUi.list.add', 'Ajouter un client')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {listError && (
          <Banner
            tone="danger"
            actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchClients}>{t('clientsUi.retry', 'Réessayer')}</button>}
          >
            {listError}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        <Tabs
          label={t('clientsInstructions.pageTitle', 'Clients B2B')}
          active={view}
          onChange={setView}
          tabs={[
            { key: 'clients', label: t('clientsUi.tabs.clients', 'Clients'), icon: Building2, count: loading ? undefined : formatCount(summary.total) },
            { key: 'activity', label: t('clientsUi.tabs.activity', 'Activité'), icon: BarChart3 },
            { key: 'souffrance', label: t('clientsUi.tabs.souffrance', 'Avis de souffrance'), icon: BellRing },
          ]}
        />
        <div role="tabpanel">
          {view === 'clients' && renderClientsView()}
          {view === 'activity' && renderActivityView()}
          {view === 'souffrance' && renderSouffranceView()}
        </div>
      </div>

      {showCreateModal && (
        <Modal
          wide
          icon={Building2}
          title={t('clientsUi.create.title', 'Ajouter un client B2B')}
          description={t('clientsUi.create.desc', 'Ses tickets lui seront rattachés à l’arrivée ; ses consignes et ses transporteurs s’appliqueront ensuite. Seul le nom est obligatoire.')}
          onClose={handleCloseCreateModal}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={handleCloseCreateModal} disabled={creating}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="submit"
                form="clients-create-form"
                className="u-btn u-btn--primary"
                data-tour="client-create"
                disabled={creating || !newClient.name.trim()}
              >
                {creating ? t('common.saving', 'Enregistrement...') : t('clientsUi.create.submit', 'Ajouter le client')}
              </button>
            </>
          )}
        >
          <form id="clients-create-form" className="u-form clients-v2__create" onSubmit={handleCreateClient} noValidate>
            {createError && <Banner tone="danger">{createError}</Banner>}
            <ClientB2BForm
              form={newClient}
              setForm={setNewClient}
              carriers={carriers}
              idPrefix="new-client"
              generalLanguage={souffrance?.language}
            />
          </form>
        </Modal>
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default ClientsInstructions;
